import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import DrawerScrim, { DRAWER_OVERLAY_CLASS } from 'shared/components/ui/DrawerScrim';
import MetaGrid from 'shared/components/ui/MetaGrid';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import DetailHoverCard from 'shared/components/ui/DetailHoverCard';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import ConversationDetailDrawer from 'features/agent-analytics/components/ConversationDetailDrawer';
import AskLucidButton from 'features/agent-analytics/components/AskLucidButton';
import {
  CopyButton, FeedbackCell, StatusPill, ThumbDownIcon, ThumbUpIcon,
  formatCost, formatDateTime, formatDuration, formatNumber, formatRelativeTime, truncate,
} from 'features/agent-analytics/components/runCells';
import { presetToISORange, DEFAULT_LIST_PRESET } from 'shared/utils/timePresets';
import { getUserProfile, getUserProfileList } from 'features/users/userProfileApi';
import DrawerTab from 'shared/components/ui/DrawerTab';
import { Card, Donut, NAMED_PALETTE } from 'features/agent-analytics/components/failures/FailuresSummary';

/**
 * An end user, seen through their agent runs. Opened in place from anywhere a
 * distinct_id shows (see openUserProfile); runs and conversations stack on top
 * of it, the same way they do over the conversation drawer. Everything below
 * the header is for the chosen period (last 30 days to start).
 *
 * Opening it loads the header and Overview only. Runs, Conversations and
 * Feedback each load their first 50 when their tab is first shown, and more on
 * "Load more"; their badges are counts from the profile. UserProfileHost keys
 * this drawer by user, so a different user starts fresh.
 */
export default function UserProfileDrawer({ distinctId, teamId, teamSlug, onClose }) {
  const [preset, setPreset] = useState(DEFAULT_LIST_PRESET);
  const [range, setRange] = useState(() => presetToISORange(DEFAULT_LIST_PRESET));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [openRunId, setOpenRunId] = useState(null);
  const [openConversation, setOpenConversation] = useState(null);
  const listArgs = { teamId, distinctId, range };
  const runs = useProfileList('runs', tab === 'runs', listArgs);
  const conversations = useProfileList('conversations', tab === 'conversations', listArgs);
  const feedback = useProfileList('feedback', tab === 'feedback', listArgs);

  useEffect(() => {
    if (!distinctId || !teamId || !range) return undefined;
    let live = true;
    setLoading(true); setError(null);
    getUserProfile({ teamId, distinctId, from: range.from, to: range.to })
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e?.response?.data?.error || 'Could not load this user'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [distinctId, teamId, range]);

  // Esc closes the top-most layer only: a stacked run or conversation first.
  // Read in the CAPTURE phase, before any drawer handles the key: the run
  // drawer closes itself on the same press, React re-renders synchronously,
  // and a bubble-phase listener re-registered by that render would see nothing
  // stacked and close this drawer too.
  const stackedRef = useRef(false);
  stackedRef.current = Boolean(openRunId || openConversation);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || stackedRef.current) return;
      // Esc inside a field or the period picker is theirs, not the drawer's.
      if (e.target?.closest?.('input, textarea, select, [role="dialog"], [role="menu"], [role="listbox"]')) return;
      // This drawer is the top layer: close it and keep the key from also
      // closing a drawer of the page underneath.
      e.stopPropagation();
      onClose?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const onPeriod = (next) => {
    const p = next?.preset || (next?.type === 'custom' ? 'custom' : '');
    setPreset(p || DEFAULT_LIST_PRESET);
    const nextRange = next?.from || next?.to
      ? { from: next.from || range.from, to: next.to || new Date().toISOString() }
      : presetToISORange(p) || range;
    // The same window again (the picker re-applied) keeps the loaded profile
    // and lists instead of fetching them all over.
    if (nextRange.from !== range?.from || nextRange.to !== range?.to) setRange(nextRange);
  };

  const t = data?.totals;
  const props = data?.identity?.custom_properties || {};
  // A name only when it adds something: many teams use the email as the id.
  const nameRaw = props.name || props.full_name || props.email || null;
  const name = nameRaw && String(nameRaw) !== distinctId ? String(nameRaw) : null;
  const periodLabel = range ? `${fmtDay(range.from)} – ${fmtDay(range.to)}` : '';

  // Runs and conversations are counted on their tab badges right below, so
  // the strip leaves them out; the detail behind each figure is on hover.
  const metaCells = useMemo(() => {
    if (!t) return [];
    const ev = data.evals;
    const fb = data.feedback;
    return [
      name && { label: 'Name', title: name, content: <span>{name}</span> },
      { label: 'Last active', title: titleTime(t.last_run_at), content: <span className="tabular-nums">{formatRelativeTime(t.last_run_at)}</span> },
      { label: 'First seen', content: <span className="tabular-nums">{formatDateTime(t.first_run_at)}</span> },
      {
        label: 'Errors',
        content: (
          <DetailHoverCard
            title="Runs"
            rows={[
              { label: 'Runs', value: formatNumber(t.runs) },
              { label: 'Errors', value: formatNumber(t.errors) },
              { label: 'Conversations', value: formatNumber(t.conversations) },
              { label: 'Agents', value: formatNumber(t.agents) },
              t.avg_duration_ms != null && { label: 'Avg duration', value: formatDuration(t.avg_duration_ms) },
            ]}
          >
            <span className={`tabular-nums ${t.errors ? 'text-[color:var(--feedback-error)]' : ''}`}>
              {formatNumber(t.errors)}{t.runs ? ` (${pct(t.errors, t.runs)}%)` : ''}
            </span>
          </DetailHoverCard>
        ),
      },
      ev.scored ? {
        label: 'Evals passed',
        content: (
          <DetailHoverCard
            title="Eval results"
            rows={[
              { label: 'Passed', value: formatNumber(ev.passed) },
              { label: 'Failed', value: formatNumber(ev.failed) },
              { label: 'Evals', value: formatNumber(ev.by_eval.length) },
            ]}
          >
            <span className="tabular-nums">{pct(ev.passed, ev.scored)}%</span>
          </DetailHoverCard>
        ),
      } : null,
      fb.count ? {
        label: 'Feedback',
        content: (
          <DetailHoverCard
            title="Feedback"
            rows={[
              { label: 'Given', value: formatNumber(fb.count) },
              { label: 'Positive', value: formatNumber(fb.positive) },
              { label: 'Negative', value: formatNumber(fb.negative) },
              fb.avg_rating != null && { label: 'Avg rating', value: fb.avg_rating.toFixed(1) },
            ]}
          >
            <span className="inline-flex items-center gap-2 tabular-nums">
              <span className="inline-flex items-center gap-1"><ThumbUpIcon />{fb.positive}</span>
              <span className="inline-flex items-center gap-1"><ThumbDownIcon />{fb.negative}</span>
            </span>
          </DetailHoverCard>
        ),
      } : null,
      {
        label: 'Cost',
        content: (
          <DetailHoverCard
            title="Cost"
            rows={[
              { label: 'Cost', value: formatCost(t.cost) },
              { label: 'Tokens in', value: formatNumber(t.tokens_in) },
              { label: 'Tokens out', value: formatNumber(t.tokens_out) },
            ]}
          >
            <span className="tabular-nums">{formatCost(t.cost)}</span>
          </DetailHoverCard>
        ),
      },
    ].filter(Boolean);
  }, [t, data, name]);

  if (!distinctId) return null;

  const evalHref = (lineageId) => `/${encodeURIComponent(teamSlug || '')}/agent-analytics/evals?eval=${encodeURIComponent(lineageId)}&filter=failed`;

  return (
    <>
      <div className={DRAWER_OVERLAY_CLASS} role="region" aria-label="User profile">
        <DrawerScrim onClose={onClose} />
        <section
          className="pointer-events-auto relative flex h-full w-[min(100%,880px)] flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl sm:w-[min(92%,880px)]"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 pt-4">
            {/* The run drawer's control row: h-8 controls, gap-3, a 28px close. */}
            <div className="flex items-center gap-3">
              {/* CopyButton is hidden until a group/row is hovered; without one it never showed. */}
              <div className="group/row flex min-w-0 items-center gap-1">
                <span className="min-w-0 truncate font-mono text-[13px] font-medium text-[color:var(--text-primary)]" title={distinctId}>
                  {distinctId}
                </span>
                <CopyButton value={distinctId} label="Copy user id" />
              </div>
              <div className="ml-auto flex flex-none items-center gap-3">
                <EventsTimeFilter selectedPreset={preset} onTimeFilterChange={onPeriod} />
                <AskLucidButton
                  prompt={`Analyze the end user "${distinctId}" over ${periodLabel}. `}
                  title="Ask Lucid about this user"
                />
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {error ? (
              <ErrorNote>{error}</ErrorNote>
            ) : !data ? (
              <div className="flex justify-center py-12"><TrodoLoader size={64} label="Loading user" /></div>
            ) : (
              <>
                <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                  <MetaGrid cells={metaCells} />
                </div>
                <div className="-mx-4 flex min-h-0 flex-1 flex-col overflow-hidden border-t border-[color:var(--border-default)] bg-surface">
                  <div className="flex flex-none items-center gap-1 overflow-x-auto border-b border-[color:var(--border-default)] bg-surface px-3">
                    <Tab id="overview" label="Overview" tab={tab} onClick={setTab} />
                    <Tab id="runs" label="Runs" tab={tab} onClick={setTab} badge={t.runs || null} />
                    <Tab id="conversations" label="Conversations" tab={tab} onClick={setTab} badge={t.conversations || null} />
                    <Tab id="evals" label="Evals" tab={tab} onClick={setTab} badge={data.evals.by_eval.length || null} />
                    <Tab id="feedback" label="Feedback" tab={tab} onClick={setTab} badge={data.feedback.count || null} />
                    <Tab id="properties" label="Properties" tab={tab} onClick={setTab} badge={Object.keys(props).length || null} />
                  </div>
                  <div className={`min-h-0 flex-1 overflow-y-auto p-3 ${loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}`}>
                    {tab === 'overview' ? (
                      <Overview data={data} onOpenRun={setOpenRunId} onTab={setTab} />
                    ) : tab === 'runs' ? (
                      <Paged list={runs} total={t.runs} empty="No runs in this period">
                        {(rows) => <RunsTable runs={rows} onOpen={setOpenRunId} />}
                      </Paged>
                    ) : tab === 'conversations' ? (
                      <Paged list={conversations} total={t.conversations} empty="No conversations in this period">
                        {(rows) => <ConversationsTable rows={rows} onOpen={setOpenConversation} />}
                      </Paged>
                    ) : tab === 'evals' ? (
                      <EvalsTable rows={data.evals.by_eval} evalHref={evalHref} scope={data.scope} totalRuns={t.runs} />
                    ) : tab === 'feedback' ? (
                      <Paged list={feedback} total={data.feedback.count} empty="No feedback in this period">
                        {(rows) => <FeedbackList rows={rows} onOpenRun={setOpenRunId} head />}
                      </Paged>
                    ) : (
                      <PropertiesTable props={props} updatedAt={data.identity?.custom_properties_last_updated} />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {openConversation ? (
        <ConversationDetailDrawer
          conversationId={openConversation.conversation_id}
          teamId={teamId}
          onClose={() => setOpenConversation(null)}
        />
      ) : null}
      {openRunId ? (
        <AgentRunDetailPage runId={openRunId} teamId={teamId} onClose={() => setOpenRunId(null)} />
      ) : null}
    </>
  );
}

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

/** 1 Oct 2026: a day, for the chart's axis and the period in Lucid's draft. */
function fmtDay(iso) {
  if (!iso) return '—';
  try { return format(new Date(iso), 'd MMM yyyy'); } catch { return String(iso); }
}

/** The exact moment behind a relative time, as the trace drawers write it; nothing when there is none. */
const titleTime = (iso) => (iso ? formatDateTime(iso) : undefined);

const EMPTY_LIST = { rows: null, hasMore: false, loading: false, error: null };

/**
 * One tab's list for this user and period: nothing until the tab is first
 * shown, then 50 rows at a time. A new period empties it; it loads again the
 * next time its tab is shown (straight away when that is now).
 */
function useProfileList(kind, active, { teamId, distinctId, range }) {
  const [list, setList] = useState(EMPTY_LIST);
  const key = `${teamId}|${distinctId}|${range?.from}|${range?.to}`;
  const keyRef = useRef(key);
  const inFlight = useRef(false);

  useEffect(() => {
    if (keyRef.current === key) return;
    keyRef.current = key;
    inFlight.current = false;
    setList(EMPTY_LIST);
  }, [key]);

  const load = useCallback(async (offset) => {
    if (inFlight.current || !distinctId || !teamId || !range) return;
    inFlight.current = true;
    const at = keyRef.current;
    setList((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const out = await getUserProfileList(kind, { teamId, distinctId, from: range.from, to: range.to, offset });
      if (keyRef.current !== at) return;
      setList((cur) => ({ rows: offset ? [...(cur.rows || []), ...out.rows] : out.rows, hasMore: Boolean(out.has_more), loading: false, error: null }));
    } catch (e) {
      if (keyRef.current === at) setList((cur) => ({ ...cur, loading: false, error: e?.response?.data?.error || 'Could not load this list' }));
    } finally {
      if (keyRef.current === at) inFlight.current = false;
    }
  }, [kind, teamId, distinctId, range]);

  useEffect(() => {
    if (active && list.rows === null && !list.loading && !list.error) load(0);
  }, [active, list.rows, list.loading, list.error, load]);

  return { ...list, more: () => load(list.rows?.length || 0) };
}

/** A tab's list: a loader until its first page, then the rows and "Load more" while there are more. */
function Paged({ list, total, empty, children }) {
  if (!list.rows) {
    return list.error
      ? <ErrorNote>{list.error}</ErrorNote>
      : <div className="flex justify-center py-8"><TrodoLoader size={40} label="Loading" /></div>;
  }
  if (!list.rows.length) return <Empty>{empty}</Empty>;
  return (
    <>
      {children(list.rows)}
      <div className="flex items-center gap-3 pt-2 text-[11px] text-[color:var(--text-muted)]">
        {total > list.rows.length ? <span className="tabular-nums">{formatNumber(list.rows.length)} of {formatNumber(total)}</span> : null}
        {list.hasMore ? (
          <button
            type="button"
            onClick={list.more}
            disabled={list.loading}
            className="inline-flex h-7 items-center rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover disabled:opacity-60"
          >
            {list.loading ? 'Loading…' : 'Load more'}
          </button>
        ) : null}
        {list.error ? <span className="text-[color:var(--feedback-error)]">{list.error}</span> : null}
      </div>
    </>
  );
}

// The shared drawer tab, so this strip matches the run and conversation
// drawers that open over it.
function Tab({ id, label, tab, badge, onClick }) {
  return (
    <DrawerTab id={id} label={label} active={tab === id} badge={badge != null ? formatNumber(badge) : null} onClick={onClick} />
  );
}

const TH = 'whitespace-nowrap px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]';
const TD = 'px-2 py-1.5 align-top';
const ROW_CLICK = 'cursor-pointer border-t border-[color:var(--border-subtle)] hover:bg-surface-hover';
const ROW = 'border-t border-[color:var(--border-subtle)]';
const NUM = `${TD} text-right tabular-nums`;
const WHEN = `${TD} whitespace-nowrap tabular-nums text-[color:var(--text-muted)]`;

/** Tables scroll sideways on a narrow screen instead of squeezing their columns. */
function Table({ children, minWidth = 560 }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" style={{ minWidth }}>{children}</table>
    </div>
  );
}

/** A table's header row: one style for every table in the drawer. */
function Head({ cols }) {
  return (
    <thead>
      <tr>{cols.map(([label, right]) => <th key={label} className={`${TH} ${right ? 'text-right' : ''}`}>{label}</th>)}</tr>
    </thead>
  );
}

const NOTE = 'py-8 text-center text-[12px]';

function Empty({ children }) {
  return <div className={`${NOTE} text-[color:var(--text-muted)]`}>{children}</div>;
}

function ErrorNote({ children }) {
  return <div className={`${NOTE} text-[color:var(--feedback-error)]`}>{children}</div>;
}

/** Scope note shown only when the user has more runs in the period than evals and feedback cover. */
function ScopeNote({ scope, totalRuns }) {
  if (!scope || totalRuns <= scope.recent_runs) return null;
  return <div className="pt-2 text-[11px] text-[color:var(--text-muted)]">From the latest {formatNumber(scope.recent_runs)} of {formatNumber(totalRuns)} runs</div>;
}

/**
 * Each fact once. The pass rate and run / error totals are in the meta strip
 * above, so there is no pass / fail donut here; the agents are one table, the
 * column header naming it, not a titled card around it.
 */
function Overview({ data, onOpenRun, onTab }) {
  const comments = data.feedback.comments || [];
  if (!data.totals.runs) return <Empty>No runs in this period</Empty>;
  const ev = data.evals;
  const outcome = [
    { key: 'passed', label: 'Passed', count: ev.passed, color: 'var(--feedback-success)' },
    { key: 'failed', label: 'Failed', count: ev.failed, color: 'var(--feedback-error)' },
  ].filter((c) => c.count > 0);
  // Which evals fail is just a breakdown: distinct hues, no judgement, the
  // same donuts the Issues page uses.
  const failing = ev.by_eval.filter((e) => e.failed > 0).sort((a, b) => b.failed - a.failed);
  const top = failing.slice(0, 6).map((e, i) => ({ key: e.lineage_id, label: e.name, count: e.failed, color: NAMED_PALETTE[i % NAMED_PALETTE.length] }));
  const rest = failing.slice(6).reduce((n, e) => n + e.failed, 0);
  const byEval = rest ? [...top, { key: 'other', label: 'Other evals', count: rest, color: 'color-mix(in srgb, var(--text-muted) 60%, transparent)', other: true }] : top;
  return (
    // Two columns: the series and the tables span both, the two eval donuts
    // sit side by side.
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Card title={`Runs per ${data.series.bucket}`} action={<span className="ml-auto text-[11px] text-[color:var(--text-muted)]"><Legend /></span>} className="md:col-span-2">
        <SeriesBars series={data.series} />
      </Card>

      {ev.scored ? (
        <>
          <Card title="Eval results" sub={`${formatNumber(ev.by_eval.length)} evals`}>
            <Donut items={outcome} total={ev.scored} />
          </Card>
          <Card
            title="Failures by eval"
            action={<button type="button" onClick={() => onTab('evals')} className="ml-auto text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">All evals</button>}
          >
            {byEval.length ? <Donut items={byEval} total={ev.failed} /> : (
              <div className="flex h-[132px] items-center justify-center text-[12px] text-[color:var(--text-muted)]">No failures</div>
            )}
          </Card>
        </>
      ) : null}

      {/* The agents table alone: it used to sit in a card titled "Agents"
          above a column also headed Agent. */}
      <div className="rounded-md border border-[color:var(--border-default)] bg-surface px-1 pb-1 shadow-token-sm md:col-span-2">
        <Table minWidth={420}>
          <Head cols={[['Agent'], ['Runs', 1], ['Errors', 1], ['Cost', 1], ['Last run', 1]]} />
          <tbody>
            {data.agents.map((a) => (
              <tr key={a.agent_name} className={ROW}>
                <td className={TD}>{a.agent_name}</td>
                <td className={NUM}>{formatNumber(a.runs)}</td>
                <td className={`${NUM} ${a.errors ? 'text-[color:var(--feedback-error)]' : 'text-[color:var(--text-muted)]'}`}>{formatNumber(a.errors)}</td>
                <td className={NUM}>{formatCost(a.cost)}</td>
                <td className={`${WHEN} text-right`} title={titleTime(a.last_at)}>{formatRelativeTime(a.last_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {comments.length ? (
        <Card title="Latest feedback" className="md:col-span-2">
          <FeedbackList rows={comments} onOpenRun={onOpenRun} />
        </Card>
      ) : null}
    </div>
  );
}

function Legend() {
  return (
    <span className="inline-flex items-center gap-3">
      <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[color:var(--trodo-primary)] opacity-70" />Runs</span>
      <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[color:var(--feedback-error)]" />Errors</span>
    </span>
  );
}

/** Runs per day (or week, past ~3 months), errors stacked at the base. Every bucket in the period, zeros included. */
function SeriesBars({ series }) {
  const step = series.bucket === 'week' ? 7 : 1;
  const byDay = new Map(series.rows.map((r) => [r.day, r]));
  const buckets = [];
  const start = new Date(series.from);
  // Align to the bucket the server truncates to: UTC midnight, Monday for weeks.
  let d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  if (step === 7) d = new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * 86400000);
  const end = new Date(series.to).getTime();
  while (d.getTime() <= end && buckets.length < 400) {
    const key = d.toISOString().slice(0, 10);
    buckets.push({ key, runs: byDay.get(key)?.runs || 0, errors: byDay.get(key)?.errors || 0 });
    d = new Date(d.getTime() + step * 86400000);
  }
  const max = Math.max(1, ...buckets.map((b) => b.runs));
  const H = 72;
  return (
    <div>
      <div className="flex items-end gap-[2px]" style={{ height: H }}>
        {buckets.map((b) => {
          const h = Math.round((b.runs / max) * H);
          const eh = b.runs ? Math.max(b.errors ? 2 : 0, Math.round((b.errors / b.runs) * h)) : 0;
          return (
            <div
              key={b.key}
              className="flex min-w-[2px] flex-1 flex-col justify-end"
              title={`${step === 7 ? 'Week of ' : ''}${fmtDay(b.key)}: ${formatNumber(b.runs)} runs${b.errors ? `, ${formatNumber(b.errors)} errors` : ''}`}
            >
              {h - eh > 0 ? <div style={{ height: h - eh }} className="rounded-t-sm bg-[color:var(--trodo-primary)] opacity-70" /> : null}
              {eh ? <div style={{ height: eh }} className={`${h - eh > 0 ? '' : 'rounded-t-sm'} bg-[color:var(--feedback-error)]`} /> : null}
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] tabular-nums text-[color:var(--text-disabled)]">
        <span>{fmtDay(buckets[0]?.key)}</span><span>{fmtDay(buckets[buckets.length - 1]?.key)}</span>
      </div>
    </div>
  );
}

function EvalPills({ evals }) {
  if (!evals) return <span className="text-[color:var(--text-muted)]">—</span>;
  return (
    <span className="inline-flex gap-2 tabular-nums">
      {evals.passed ? <span className="text-[color:var(--feedback-success)]">{evals.passed} passed</span> : null}
      {evals.failed ? <span className="text-[color:var(--feedback-error)]">{evals.failed} failed</span> : null}
    </span>
  );
}

function RunsTable({ runs, onOpen }) {
  return (
    <Table minWidth={760}>
      <Head cols={[['When'], ['Agent'], ['Status'], ['Input'], ['Evals'], ['Feedback'], ['Duration', 1], ['Cost', 1]]} />
      <tbody>
        {runs.map((r) => (
          <tr key={r.run_id} className={ROW_CLICK} onClick={() => onOpen(r.run_id)}>
            <td className={WHEN} title={titleTime(r.started_at)}>{formatRelativeTime(r.started_at)}</td>
            <td className={`${TD} whitespace-nowrap`}>{r.agent_name}</td>
            <td className={TD}><StatusPill status={r.status} /></td>
            <td className={`${TD} max-w-[240px] truncate text-[color:var(--text-secondary)]`} title={r.input_preview || ''}>{truncate(r.input_preview || '', 60)}</td>
            <td className={`${TD} whitespace-nowrap`}><EvalPills evals={r.evals} /></td>
            <td className={TD}><FeedbackCell row={r} /></td>
            <td className={NUM}>{formatDuration(r.duration_ms)}</td>
            <td className={NUM}>{formatCost(r.total_cost)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function ConversationsTable({ rows, onOpen }) {
  return (
    <Table minWidth={520}>
      <Head cols={[['Last activity'], ['Conversation'], ['Agents'], ['Runs', 1], ['Errors', 1]]} />
      <tbody>
        {rows.map((c) => (
          <tr key={c.conversation_id} className={ROW_CLICK} onClick={() => onOpen(c)}>
            <td className={WHEN} title={titleTime(c.last_at)}>{formatRelativeTime(c.last_at)}</td>
            <td className={`${TD} font-mono text-[11px]`} title={c.conversation_id}>{truncate(c.conversation_id, 28)}</td>
            <td className={TD}>{c.agents?.length ? c.agents.join(', ') : '—'}</td>
            <td className={NUM}>{formatNumber(c.runs)}</td>
            <td className={`${NUM} ${c.errors ? 'text-[color:var(--feedback-error)]' : 'text-[color:var(--text-muted)]'}`}>{formatNumber(c.errors)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function EvalsTable({ rows, evalHref, scope, totalRuns }) {
  if (!rows.length) return <Empty>No eval results in this period</Empty>;
  return (
    <>
      <Table minWidth={560}>
        <Head cols={[['Eval'], ['On'], ['Passed', 1], ['Failed', 1], ['Pass rate', 1], ['Last failure', 1]]} />
        <tbody>
          {rows.map((e) => (
            <tr key={e.lineage_id} className={ROW}>
              <td className={TD}><a href={evalHref(e.lineage_id)} className="text-[color:var(--text-primary)] underline-offset-2 hover:underline">{e.name}</a></td>
              <td className={`${TD} text-[color:var(--text-muted)]`}>{e.target_kind}</td>
              <td className={NUM}>{formatNumber(e.passed)}</td>
              <td className={`${NUM} ${e.failed ? 'text-[color:var(--feedback-error)]' : 'text-[color:var(--text-muted)]'}`}>{formatNumber(e.failed)}</td>
              <td className={NUM}>{pct(e.passed, e.scored)}%</td>
              <td className={`${WHEN} text-right`} title={titleTime(e.last_failed_at)}>{e.last_failed_at ? formatRelativeTime(e.last_failed_at) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <ScopeNote scope={scope} totalRuns={totalRuns} />
    </>
  );
}

/** Feedback rows; `head` on the Feedback tab, where the list stands alone (the Overview card is titled). */
function FeedbackList({ rows, onOpenRun, head = false }) {
  return (
    <Table minWidth={420}>
      {head ? <Head cols={[['When'], ['Feedback'], ['Comment'], ['Agent', 1]]} /> : null}
      <tbody>
        {rows.map((f, i) => (
          <tr key={`${f.run_id}-${i}`} className={ROW_CLICK} onClick={() => onOpenRun(f.run_id)}>
            <td className={`${WHEN} w-24`} title={titleTime(f.timestamp)}>{formatRelativeTime(f.timestamp)}</td>
            <td className={`${TD} w-20`}><FeedbackCell row={{ feedback_satisfaction: f.satisfaction, feedback_rating: f.rating }} /></td>
            <td className={`${TD} text-[color:var(--text-primary)]`}>{f.comment || <span className="text-[color:var(--text-muted)]">—</span>}</td>
            <td className={`${TD} whitespace-nowrap text-right text-[color:var(--text-muted)]`}>{f.agent_name}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function PropertiesTable({ props, updatedAt }) {
  const keys = Object.keys(props).sort();
  if (!keys.length) return <Empty>No properties</Empty>;
  return (
    <>
      <Table minWidth={320}>
        <Head cols={[['Property'], ['Value']]} />
        <tbody>
          {keys.map((k) => {
            const v = props[k];
            const text = v != null && typeof v === 'object' ? JSON.stringify(v) : String(v ?? '');
            return (
              <tr key={k} className={ROW}>
                <td className={`${TD} w-48 font-mono text-[11px] text-[color:var(--text-muted)]`}>{k}</td>
                <td className={`${TD} break-all text-[color:var(--text-primary)]`}>{text}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {updatedAt ? <div className="pt-2 text-[11px] tabular-nums text-[color:var(--text-muted)]">Updated {formatDateTime(updatedAt)}</div> : null}
    </>
  );
}
